// Visual designer model: {w, h, root}; nodes: mull/split {t, dir: "cols"|"rows", sizes, kids}, unit {t:"unit", kind, sec, shape_id, ext_color}, sec {t:"sec", type, hinge, swing, slide, grid, screen_id}.
import { layoutText, parseLayout } from "@/lib/layouts";

// Design → catalog layout text (first unit's sections; X = any opening type, O = fixed). Sizes are not kept (equal split).
export function toLayout(d) {
  const unitNode = d.root.t === "unit" ? d.root : d.root.kids?.find((k) => k.t === "unit") || d.root;
  const conv = (n) => (n.t === "sec" ? ["cell", n.type === "fixed" ? "O" : "X"] : [n.dir, n.kids.map(conv)]);
  return layoutText(conv(unitNode.t === "unit" ? unitNode.sec : unitNode));
}

export const MIN = 12;
export const SEC_TYPES = [["fixed", "Fixed"], ["casement", "Casement"], ["awning", "Awning"], ["hopper", "Hopper"], ["tilt_turn", "Tilt-Turn"], ["sliding", "Sliding"], ["single_hung", "Single hung"], ["double_hung", "Double hung"], ["door", "Door leaf"]];
export const HUNG = ["single_hung", "double_hung"];
// Top / bottom sash heights of a hung section: typed top sash height (min 12 in each), else equal.
export const hungSplit = (n, h) => { const t = Number(n.top_h) || 0; const top = t >= 12 && t <= h - 12 ? t : h / 2; return [top, h - top]; };
const clone = (x) => JSON.parse(JSON.stringify(x));
export const sec = (type = "fixed", extra = {}) => ({ t: "sec", type, hinge: "left", swing: "in", slide: "right", fill: "glass", grid: null, handle: "standard", lock: "standard", screen_id: null, ...extra });
export const unit = (kind = "window", role = "main", extra = {}) => ({ t: "unit", kind, role, top: "rect", sec: sec(kind === "door" && role === "main" ? "door" : "fixed"), shape_id: null, ...extra });
export const newDesign = (kind = "window", w = 72, h = 60) => ({ w, h, root: unit(kind) });
export const newGrid = (pattern = "colonial") => ({ type: "btg", pattern, rows: 2, cols: 2, border: 3, bars: [] });

export const get = (root, path) => path.reduce((n, i) => (n.t === "unit" ? n.sec : n.kids[i]), root);
const childOf = (n, i) => (n.t === "unit" ? n.sec : n.kids[i]);
function setAt(d, path, fn) {
  const out = clone(d);
  if (!path.length) { out.root = fn(out.root); return out; }
  let n = out.root;
  for (const i of path.slice(0, -1)) n = childOf(n, i);
  const last = path[path.length - 1];
  if (n.t === "unit") n.sec = fn(n.sec); else n.kids[last] = fn(n.kids[last]);
  return out;
}

function sizesOf(n, total) {
  const s = (n.sizes || []).map(Number);
  return s.length === n.kids.length ? s : n.kids.map(() => total / n.kids.length);
}

// Flatten: units, sections, dividers with rects in inches (top-left origin). Paths: unit -> sec uses index 0.
export function layout(d) {
  const out = { units: [], secs: [], dividers: [] };
  const walk = (n, path, x, y, w, h, u) => {
    if (n.t === "unit") {
      const uu = { node: n, path, x, y, w, h };
      out.units.push(uu);
      return walk(n.sec, [...path, 0], x, y, w, h, uu);
    }
    if (n.t === "sec") return out.secs.push({ node: n, path, x, y, w, h, unit: u });
    const cols = n.dir === "cols";
    const sizes = sizesOf(n, cols ? w : h);
    let cx = x, cy = y;
    n.kids.forEach((k, i) => {
      const kw = cols ? sizes[i] : w, kh = cols ? h : sizes[i];
      walk(k, [...path, i], cx, cy, kw, kh, u);
      if (i < n.kids.length - 1) out.dividers.push({ path, i, cols, mull: n.t === "mull", x: cols ? cx + kw : x, y: cols ? y : cy + kh, len: cols ? h : w });
      if (cols) cx += kw; else cy += kh;
    });
  };
  walk(d.root, [], 0, 0, Number(d.w) || 1, Number(d.h) || 1, null);
  return out;
}

// Smallest size a node can take along a direction.
function minSize(n, cols) {
  if (n.t === "sec") return MIN;
  if (n.t === "unit") return minSize(n.sec, cols);
  const m = n.kids.map((k) => minSize(k, cols));
  return (n.dir === "cols") === cols ? m.reduce((a, b) => a + b, 0) : Math.max(...m);
}
export const minOf = (d) => ({ w: minSize(d.root, true), h: minSize(d.root, false) });

export function rectOf(d, path) {
  const L = layout(d);
  return [...L.secs, ...L.units].find((r) => r.path.join() === path.join());
}

// Move divider i of the container at path by delta inches; returns [design, clamped].
export function resize(d, path, i, delta, startSizes) {
  const r = path.length ? containerRect(d, path) : { w: d.w, h: d.h };
  const n = get(d.root, path);
  const cols = n.dir === "cols";
  const sizes = [...(startSizes || sizesOf(n, cols ? r.w : r.h))];
  const lo = minSize(n.kids[i], cols), hi = minSize(n.kids[i + 1], cols);
  let a = sizes[i] + delta;
  const pair = sizes[i] + sizes[i + 1];
  const clamped = a < lo || pair - a < hi;
  a = Math.min(Math.max(a, lo), pair - hi);
  sizes[i] = Math.round(a * 8) / 8;
  sizes[i + 1] = Math.round((pair - sizes[i]) * 8) / 8;
  return [setAt(d, path, (x) => ({ ...x, sizes })), clamped];
}

function containerRect(d, path) {
  const L = layout(d);
  const hit = [...L.units, ...L.secs].find((r) => r.path.join().startsWith(path.join()) && r.path.length > path.length);
  if (!hit) return { w: d.w, h: d.h };
  // rebuild bounds of the container from its leaves
  const leaves = [...L.units, ...L.secs].filter((r) => r.path.slice(0, path.length).join() === path.join() && r.path.length > path.length);
  const x1 = Math.min(...leaves.map((r) => r.x)), y1 = Math.min(...leaves.map((r) => r.y));
  return { w: Math.max(...leaves.map((r) => r.x + r.w)) - x1, h: Math.max(...leaves.map((r) => r.y + r.h)) - y1 };
}

export function setSize(d, path, i, value) {
  const n = get(d.root, path);
  const r = path.length ? containerRect(d, path) : { w: d.w, h: d.h };
  const sizes = sizesOf(n, n.dir === "cols" ? r.w : r.h);
  const j = i < sizes.length - 1 ? i : i - 1;
  const delta = i === j ? value - sizes[i] : sizes[i] - value;
  return resize(d, path, j, delta, sizes);
}

export function split(d, path, dir) {
  const r = rectOf(d, path);
  const total = dir === "cols" ? r.w : r.h;
  if (total < MIN * 2) return [d, true];
  return [setAt(d, path, (s) => ({ t: "split", dir, sizes: [total / 2, total / 2], kids: [s, clone({ ...s, screen_id: null })] })), false];
}

export function merge(d, path) {
  if (path.length < 2) return d;
  const parent = path.slice(0, -1);
  const p = get(d.root, parent);
  if (p.t !== "split") return d;
  return setAt(d, parent, () => p.kids.find((k) => k.t === "sec") || p.kids[0]);
}

export const patchAt = (d, path, p) => setAt(d, path, (n) => ({ ...n, ...p }));

export function addUnit(d, side, size, kind = "window", role = "unit", extra = {}) {
  const cols = side === "left" || side === "right";
  const u = unit(kind, role, extra);
  const out = clone(d);
  const first = side === "left" || side === "top";
  const r = out.root;
  const span = cols ? out.w : out.h;
  if (r.t === "mull" && (r.dir === "cols") === cols) {
    const sizes = sizesOf(r, span);
    r.kids = first ? [u, ...r.kids] : [...r.kids, u];
    r.sizes = first ? [size, ...sizes] : [...sizes, size];
  } else {
    out.root = { t: "mull", dir: cols ? "cols" : "rows", sizes: first ? [size, span] : [span, size], kids: first ? [u, r] : [r, u] };
  }
  if (cols) out.w = Number(out.w) + size; else out.h = Number(out.h) + size;
  return out;
}

export function removeUnit(d, unitPath) {
  if (!unitPath.length) return d;
  const parent = unitPath.slice(0, -1), i = unitPath[unitPath.length - 1];
  const p = get(d.root, parent);
  const cols = p.dir === "cols";
  const r = parent.length ? containerRect(d, parent) : { w: d.w, h: d.h };
  const sizes = sizesOf(p, cols ? r.w : r.h);
  const gone = sizes[i];
  let out = setAt(d, parent, (n) => {
    const kids = n.kids.filter((_, k) => k !== i), sz = sizes.filter((_, k) => k !== i);
    return kids.length === 1 ? kids[0] : { ...n, kids, sizes: sz };
  });
  if (!parent.length) out = { ...out, [cols ? "w" : "h"]: (cols ? d.w : d.h) - gone };
  return out;
}

const scaleNode = (n, fx, fy) => {
  if (n.t === "unit") return { ...n, sec: scaleNode(n.sec, fx, fy) };
  if (n.t === "sec") return n;
  return { ...n, sizes: (n.sizes || []).map((s) => Math.round(s * (n.dir === "cols" ? fx : fy) * 8) / 8), kids: n.kids.map((k) => scaleNode(k, fx, fy)) };
};

// Proportional resize (templates, overall size edits).
export function scale(d, W, H) {
  const out = { ...d, w: W, h: H, root: scaleNode(d.root, W / d.w, H / d.h) };
  // rounding fix: rebalance last sizes so they sum to the parent
  const fix = (n, w, h) => {
    if (n.t === "unit") return fix(n.sec, w, h);
    if (n.t === "sec") return;
    const total = n.dir === "cols" ? w : h;
    if (n.sizes?.length === n.kids.length) n.sizes[n.sizes.length - 1] = Math.round((total - n.sizes.slice(0, -1).reduce((a, b) => a + b, 0)) * 8) / 8;
    const s = sizesOf(n, total);
    n.kids.forEach((k, i) => fix(k, n.dir === "cols" ? s[i] : w, n.dir === "cols" ? h : s[i]));
  };
  fix(out.root, W, H);
  return out;
}

// Phase 10 layout text ("O|(X/O)") or slider config ("XO") -> design.
export function fromLayout(text, w, h, lineCategory = "casement", isSlider = false) {
  const opType = isSlider ? "sliding" : lineCategory === "awning" ? "awning" : lineCategory === "swing_door" ? "door" : "casement";
  const src = isSlider ? (text || "XO").split("").join("|") : text || "X";
  let t;
  try { t = parseLayout(src); } catch { t = ["cell", "X"]; }
  let xs = 0;
  const conv = (n, W, H) => {
    if (n[0] === "cell") {
      xs += 1;
      return sec(n[1] === "X" ? opType : "fixed", { hinge: xs % 2 ? "left" : "right" });
    }
    const cols = n[0] === "cols", total = cols ? W : H, s = total / n[1].length;
    return { t: "split", dir: cols ? "cols" : "rows", sizes: n[1].map(() => s), kids: n[1].map((k) => conv(k, cols ? s : W, cols ? H : s)) };
  };
  return { w, h, root: { t: "unit", kind: lineCategory.includes("door") ? "door" : "window", sec: conv(t, w, h), shape_id: null } };
}

// ---- Phase 17 builder: pieces (sidelites, transoms, stacked units), exact sizes, specs ----
export const GRID_TYPES = [["btg", "Between the glass"], ["outside", "Outside glass (applied)"], ["inside", "Inside glass (applied)"], ["sdl", "Both sides (simulated divided lite)"]];
export const GRID_TAG = { btg: "BTG", outside: "EXT", inside: "INT", sdl: "SDL" };
export const PATTERNS = [["colonial", "Colonial"], ["prairie", "Prairie"], ["custom", "Custom"], ["diamond", "Diamond"], ["sunburst", "Sunburst"]];
export const HANDLES = [["standard", "Standard (included)"], ["pull", "Small pull handle"], ["lever", "Lever handle"], ["keyed_lever", "Keyed lever handle"]];
export const LOCKS = [["standard", "Standard (included)"], ["double_hook", "Double-hook lock"], ["multipoint", "Multipoint lock"], ["keyed", "Keyed cylinder lock"]];
export const TOPS = [["rect", "Rectangle"], ["arch", "Arch"], ["half_round", "Half-round"]];
export const OPERABLE = ["casement", "tilt_turn", "awning", "hopper", "sliding", "door", "single_hung", "double_hung"];

// Largest difference between typed sizes and the size they must fill (0 = they add up).
export function sizeGap(d) {
  const walk = (n, w, h) => {
    if (n.t === "unit") return walk(n.sec, w, h);
    if (n.t === "sec" || !n.kids?.length) return 0;
    const cols = n.dir === "cols";
    const total = cols ? w : h;
    const s = (n.sizes || []).length === n.kids.length ? n.sizes.map(Number) : n.kids.map(() => total / n.kids.length);
    let gap = s.reduce((a, b) => a + b, 0) - total;
    n.kids.forEach((k, i) => { const g = walk(k, cols ? s[i] : w, cols ? h : s[i]); if (Math.abs(g) > Math.abs(gap)) gap = g; });
    return gap;
  };
  return Math.round(walk(d.root, Number(d.w), Number(d.h)) * 1000) / 1000;
}

const hasMain = (n) => (n.t === "unit" ? n.role === "main" : n.t !== "sec" && (n.kids || []).some(hasMain));

// Insert a piece next to the node at `path` (root, or the row holding the main piece for sidelites); totals grow by `size`.
function insertPiece(d, path, side, size, piece) {
  const cols = side === "left" || side === "right";
  const first = side === "left" || side === "top";
  const out = clone(d);
  const span = cols ? Number(out.w) : Number(out.h);
  const put = (n) => {
    if (n.t === "mull" && (n.dir === "cols") === cols) {
      const s = sizesOf(n, span);
      return { ...n, kids: first ? [piece, ...n.kids] : [...n.kids, piece], sizes: first ? [size, ...s] : [...s, size] };
    }
    return { t: "mull", dir: cols ? "cols" : "rows", sizes: first ? [size, span] : [span, size], kids: first ? [piece, n] : [n, piece] };
  };
  if (!path.length) out.root = put(out.root);
  else out.root.kids[path[0]] = put(out.root.kids[path[0]]);
  if (cols) out.w = Number(out.w) + size; else out.h = Number(out.h) + size;
  return out;
}

export function addSidelites(d, which, width) {
  const w = Number(width);
  const row = d.root.t === "mull" && d.root.dir === "rows" ? d.root.kids.findIndex(hasMain) : -1;
  const path = row >= 0 ? [row] : [];
  let out = d;
  for (const side of which === "both" ? ["left", "right"] : [which]) out = insertPiece(out, path, side, w, unit("window", "sidelite"));
  return out;
}

export const addTransom = (d, height, top = "rect") => insertPiece(d, [], "top", Number(height), unit("window", "transom", { top }));
export const stackPiece = (d, side, size, kind = "window") => insertPiece(d, [], side, Number(size), unit(kind, "unit"));

// Type an exact width/height for the piece at unitPath: the nearest container along that direction takes it, totals update.
export function setPieceSize(d, unitPath, dim, value) {
  const cols = dim === "w";
  const v = Number(value);
  let i = unitPath.length - 1;
  while (i >= 0 && get(d.root, unitPath.slice(0, i)).dir !== (cols ? "cols" : "rows")) i -= 1;
  const r = rectOf(d, unitPath);
  const old = cols ? r.w : r.h;
  if (!(v > 0) || Math.abs(v - old) < 0.001) return d;
  const f = v / old;
  let out = setAt(d, unitPath, (n) => scaleNode(n, cols ? f : 1, cols ? 1 : f));
  if (i < 0) return scale(out, cols ? v : out.w, cols ? out.h : v);
  const cpath = unitPath.slice(0, i), k = unitPath[i];
  const delta = v - old;
  const ext = (p) => { const n = get(out.root, p); return sizesOf(n, (cols ? out.w : out.h)); };
  const sizes = [...ext(cpath)];
  sizes[k] = Math.round((sizes[k] + delta) * 8) / 8;
  out = patchAt(out, cpath, { sizes });
  for (let j = cpath.length - 1; j >= 0; j -= 1) {  // ancestors along the same direction grow too
    const ap = cpath.slice(0, j), an = get(out.root, ap);
    if (an.dir === (cols ? "cols" : "rows")) { const s = [...sizesOf(an, 0)]; s[cpath[j]] = Math.round((s[cpath[j]] + delta) * 8) / 8; out = patchAt(out, ap, { sizes: s }); }
  }
  return { ...out, [dim]: Math.round((Number(out[dim]) + delta) * 8) / 8 };
}

// Scale the pieces so they fill the typed overall size again.
export function fitPieces(d) {
  const r = d.root;
  if (r.t !== "mull") return d;
  const sum = sizesOf(r, 0).reduce((a, b) => a + b, 0);
  const base = r.dir === "cols" ? { ...d, w: sum } : { ...d, h: sum };
  return scale(base, Number(d.w), Number(d.h));
}

export function pieceNames(d) {
  const L = layout(d);
  const main = L.units.find((u) => u.node.role === "main") || [...L.units].sort((a, b) => b.w * b.h - a.w * a.h)[0];
  const names = L.units.map((u, n) => {
    const role = u.node.role || "";
    if (u === main) return u.node.kind === "door" ? "Door" : "Window";
    if (role === "sidelite") return u.x < main.x ? "Left sidelite" : "Right sidelite";
    if (role === "transom") return "Transom";
    return `${u.node.kind === "door" ? "Door" : "Window"} ${n + 1}`;
  });
  return names.map((x, i) => (names.filter((y) => y === x).length > 1 ? `${x} ${i + 1}` : x));
}

const label = (list, k) => (list.find(([v]) => v === k) || [k, k])[1];
export function gridText(g) {
  if (!g) return "None";
  const p = g.pattern || "colonial";
  const extra = p === "colonial" ? ` ${g.cols}×${g.rows}` : p === "prairie" ? ` border ${g.border || 3} in` : p === "custom" ? ` ${(g.bars || []).length} bars` : "";
  return `${label(GRID_TYPES, g.type || "btg")}, ${label(PATTERNS, p)}${extra}`;
}

export function pieceSpecs(d) {
  const L = layout(d);
  const names = pieceNames(d);
  return L.units.map((u, i) => ({
    name: names[i], size: `${Math.round(u.w * 8) / 8}×${Math.round(u.h * 8) / 8}`, top: label(TOPS, u.node.top || "rect"), path: u.path,
    sections: L.secs.filter((s) => s.unit === u).map((s) => {
      const n = s.node, k = n.type;
      const bits = [label(SEC_TYPES, k), n.fill === "solid" ? "Solid panel" : "Glass", `Grids: ${gridText(n.fill === "solid" ? null : n.grid)}`];
      if (OPERABLE.includes(k)) {
        bits.push(`Handle: ${label(HANDLES, n.handle || "standard")}`, `Lock: ${label(LOCKS, n.lock || "standard")}`);
        if (["casement", "tilt_turn", "door"].includes(k)) bits.push(`Hinge ${n.hinge === "right" ? "Right" : "Left"}${k === "door" ? `, swing ${n.swing === "out" ? "Out" : "In"}` : ""}`);
      }
      return bits.join(" · ");
    }),
  }));
}
